"use client";

import { Clock3, FileText, Orbit, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import type { MiroFishProjectSummary } from "@/types/mirofish";

export default function MiroFishHistoryDatabase({
  items,
  loading = false,
  onSelect,
}: {
  items: MiroFishProjectSummary[];
  loading?: boolean;
  onSelect?: (item: MiroFishProjectSummary) => void;
}) {
  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="h-56 rounded-[28px] border border-white/10 bg-white/5 animate-pulse"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      {items.map((item) => (
        <button
          key={item.projectId}
          type="button"
          onClick={() => onSelect?.(item)}
          className="group rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.03))] p-5 text-left transition hover:-translate-y-1 hover:border-white/20 hover:bg-white/[0.08]"
        >
          <div className="flex items-center justify-between">
            <span className="rounded-full border border-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.24em] text-white/55">
              {item.simulationId?.replace(/^sim_/, "") || "pending"}
            </span>
            <span
              className={cn(
                "rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.2em]",
                item.status === "report_ready"
                  ? "bg-emerald-500/12 text-emerald-300"
                  : item.status.includes("simulation")
                    ? "bg-sky-500/12 text-sky-300"
                    : "bg-amber-500/12 text-amber-200"
              )}
            >
              {item.status.replace(/_/g, " ")}
            </span>
          </div>

          <h3 className="mt-5 text-xl font-semibold text-white">{item.title}</h3>
          <p className="mt-3 line-clamp-3 text-sm leading-6 text-white/65">{item.prompt}</p>

          <div className="mt-5 grid gap-2 text-xs text-white/55">
            <div className="flex items-center gap-2">
              <FileText className="h-3.5 w-3.5" />
              <span>{item.sourceDocuments.map((doc) => doc.filename).join(", ")}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock3 className="h-3.5 w-3.5" />
              <span>{new Date(item.createdAt).toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-2">
              <Orbit className="h-3.5 w-3.5" />
              <span>
                {item.currentRound}/{item.totalRounds} rounds complete
              </span>
            </div>
          </div>

          <div className="mt-5 flex items-center gap-2 text-sm text-white/75">
            <Sparkles className="h-4 w-4 text-amber-300" />
            Open this simulation
          </div>
        </button>
      ))}
    </div>
  );
}

